/**
 * Colours for the date and time pickers.
 *
 * The schedule pickers used to read their brand colours in JS, once, at mount:
 * `useMemo(() => getComputedStyle(document.documentElement).getPropertyValue('--color-primary'), [])`.
 * That read was wrong twice over, in BOTH hosts:
 *
 *   - it read `<html>`, but the dark switch (`.dark`) lives on `<body>` in the
 *     web app and on the shadow root's `.btcss` wrapper in the extension, so the
 *     value it got was ALWAYS the light one — measured in dark mode, today's date
 *     was #003750 navy on the #0f172a popover, 1.41:1;
 *   - in the extension `<html>` is WhatsApp's, where our tokens do not exist at
 *     all, so it fell back to literal hex and ignored the brand in either theme;
 *   - and with `[]` deps it could never have followed a theme flip anyway.
 *
 * So the pickers now receive CSS variables, not resolved colours. The browser
 * resolves a `var()` against whichever `.dark` / `[data-theme="dark"]` ancestor
 * the popover actually has, at paint time, every time the theme flips — nothing
 * is cached in JS, so there is nothing to go stale.
 *
 * Measured, light / dark (popover #ffffff / #0f172a):
 *   today's number   --color-primary on popover            12.63 / 8.33
 *   selected day     --color-accent-contrast on --color-accent 5.92 / 8.33
 *   selected time    --color-primary-contrast on --color-primary 12.63 / 8.33
 */
export const THEMED_DATE_PICKER_COLORS = {
    primaryColor: 'var(--color-primary)',
    lightColor: 'var(--color-accent)',
    darkColor: 'var(--color-primary)',
    onLightColor: 'var(--color-accent-contrast)',
} as const;

export const THEMED_TIME_PICKER_COLORS = {
    primaryColor: 'var(--color-primary)',
    lightColor: 'var(--color-accent)',
    darkColor: 'var(--color-primary)',
    onDarkColor: 'var(--color-primary-contrast)',
} as const;

/**
 * A faint wash of `color` for a picker's hover state.
 *
 * Was `${color}14` — hex-alpha by string concatenation, which only works on a
 * six-digit hex and yields an invalid value for anything else (a `var()`
 * included), so the declaration was silently dropped. `color-mix` accepts
 * either; 8% is the same wash as the old `14` (20/255).
 */
export const hoverWash = (color: string): string => `color-mix(in srgb, ${color} 8%, transparent)`;
